"use client"

import { useState } from "react"
import { resolveStorageUrl } from "@/lib/storage/public-url"
import { SQUARE_SM, imagePresetUrl } from "@/lib/images/presets"
import Image from "next/image"
import Link from "next/link"
import { useTranslations } from "next-intl"
import {
  ArrowLeftRight,
  Check,
  Clock,
  X,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { useOffer, useOfferActions } from "@/hooks/use-offer"
import { OfferModal } from "./offer-modal"
import { OfferDeclineConfirmDialog } from "./offer-decline-confirm-dialog"
import {
  currencySymbol,
  isPendingStatus,
} from "@/lib/offers/offer-utils"
import {
  depthFromOfferRoot,
  discountPct,
  offerCountdownShort,
  type OfferTab,
} from "@/lib/offers/offer-hub-filters"
import { cn, formatPrice } from "@/lib/utils"
import type { OfferWithRelations, OfferStatus } from "@/lib/offers/offer-types"

interface OfferCardProps {
  offer: OfferWithRelations
  currentUserId: string
  tab?: OfferTab
  offerMap?: Map<string, OfferWithRelations>
  viewerIsListingSeller?: boolean
  /**
   * Which secondary-surface dialect the card renders in: `"sheet"` = alpha
   * tints keyed off the overlay panel (`*-foreground/<alpha>`), `"page"` = the
   * page's discrete `muted` surfaces. Both are theme-dependent tokens.
   *
   * Values WERE `"dark" | "light"`. That stopped being true once the branch
   * moved off `white`/`neutral-*` literals — it picks a surface, not a colour.
   * Genuinely two-valued: `OfferDetailSheet` derives it from `isMobile`, and
   * `ListingOffersPanel` forwards its own axis.
   * @see .tondocs/ui/HANDOFF_SHEET_LIGHT_THEME_2026_08_01.md
   */
  surfaceVariant?: "page" | "sheet"
  onOpenDetail?: () => void
  modalZIndex?: number
}

export function OfferCard({
  offer,
  currentUserId,
  tab,
  offerMap,
  viewerIsListingSeller = false,
  surfaceVariant = "page",
  onOpenDetail,
  modalZIndex,
}: OfferCardProps) {
  const t = useTranslations()
  const actions = useOfferActions()
  const [counterOpen, setCounterOpen] = useState(false)
  const [declineConfirmOpen, setDeclineConfirmOpen] = useState(false)
  const [pending, setPending] = useState<null | "accept" | "decline" | "withdraw">(null)

  const slot = useOffer(offer.id, {
    initial: {
      amount: offer.amount,
      status: offer.status,
      expiresAt: offer.expires_at,
      paymentDeadline: offer.payment_deadline,
      eventVersion: offer.event_version,
      settledAt: offer.settled_at,
      declineReason: offer.decline_reason ?? null,
      parentOfferId: offer.parent_offer_id,
    },
  })

  const status = slot?.status ?? offer.status
  const amount = slot?.amount ?? offer.amount
  const settledAt = slot?.settledAt ?? offer.settled_at
  const paymentDeadline = slot?.paymentDeadline ?? offer.payment_deadline
  const isSeller = viewerIsListingSeller || currentUserId === offer.seller_id
  const isBuyer = !viewerIsListingSeller && currentUserId === offer.buyer_id
  const onSheet = surfaceVariant === "sheet"
  const symbol = currencySymbol(offer.currency)
  const listPrice = offer.listing?.price ?? null
  const disc = discountPct(amount, listPrice)
  const isAcceptedAwaitingPayment = status === "accepted" && settledAt == null

  const resolvedTab: OfferTab =
    tab ?? (isSeller && !isBuyer ? "received" : isBuyer ? "sent" : "received")

  const depth =
    offerMap && status === "pending" ? depthFromOfferRoot(offer, offerMap) : null
  const sellerActsOnPending =
    resolvedTab === "received" &&
    isSeller &&
    status === "pending" &&
    (depth === null || depth % 2 === 0)
  const buyerSentRespond =
    resolvedTab === "sent" &&
    isBuyer &&
    status === "pending" &&
    depth !== null &&
    depth % 2 === 1

  const counterparty =
    resolvedTab === "received" ? offer.buyer : offer.seller
  const username = counterparty?.username ? `@${counterparty.username}` : "@?"

  const showTimer =
    status === "pending" ||
    status === "counter_offered" ||
    isAcceptedAwaitingPayment
  const timerIso =
    isAcceptedAwaitingPayment && paymentDeadline
      ? paymentDeadline
      : slot?.expiresAt ?? offer.expires_at
  const timerLabel = offerCountdownShort(timerIso)
  const acceptanceTimerLabel = isBuyer
    ? t("offers.card.reservedForYou", { default: "Reserved for you" })
    : t("offers.card.awaitingPayment", { default: "Awaiting payment" })

  const showSellerActions = sellerActsOnPending
  const showBuyerCounterActions = buyerSentRespond
  const showPayButton = resolvedTab === "sent" && isBuyer && isAcceptedAwaitingPayment

  const handleAccept = async () => {
    setPending("accept")
    await actions.acceptOffer(offer.id)
    setPending(null)
  }
  const handleDecline = async () => {
    setPending("decline")
    await actions.declineOffer(offer.id)
    setPending(null)
    setDeclineConfirmOpen(false)
  }
  const handleWithdraw = async () => {
    setPending("withdraw")
    await actions.withdrawOffer(offer.id)
    setPending(null)
  }

  return (
    <>
      <div
        role={onOpenDetail ? "button" : undefined}
        tabIndex={onOpenDetail ? 0 : undefined}
        onClick={
          onOpenDetail
            ? (e) => {
                const target = e.target as HTMLElement
                if (target.closest("button, a")) return
                onOpenDetail()
              }
            : undefined
        }
        onKeyDown={
          onOpenDetail
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault()
                  onOpenDetail()
                }
              }
            : undefined
        }
        className={cn(
          "flex flex-col gap-2 p-3 rounded-2xl",
          onSheet && "text-foreground",
          onOpenDetail && "cursor-pointer active:bg-muted/40 transition-colors",
        )}
      >
        <div className="flex gap-3">
          <div className="relative shrink-0">
            {offer.listing?.image_urls[0] ? (
              onOpenDetail ? (
                <div className="block w-[72px] h-[72px] rounded-xl overflow-hidden bg-muted">
                  <Image
                    // 72 px square, `object-cover` -> `SQUARE_SM` (128 square).
                    // Deliberately NOT rounded up to SQUARE_LG: 128 is 11 % under
                    // the 144 px a 2x screen wants, which is invisible on a
                    // 72 px decorative tile, while 256 would be 4x the pixels.
                    // `unoptimized` STAYS and is now load-bearing — the src is
                    // already CDN-sized, so the Next optimizer would only add a
                    // hop and a billed transformation (handoff §2.4).
                    src={imagePresetUrl(offer.listing.image_urls[0], SQUARE_SM)}
                    alt=""
                    width={72}
                    height={72}
                    className="w-full h-full object-cover"
                    unoptimized
                  />
                </div>
              ) : (
                <Link href={`/listings/${offer.listing.id}`} className="block w-[72px] h-[72px] rounded-xl overflow-hidden bg-muted">
                  <Image
                    // Same tile as the non-link branch above — same preset, so
                    // the two branches share one cache entry. See there for why
                    // SQUARE_SM and why `unoptimized` is load-bearing.
                    src={imagePresetUrl(offer.listing.image_urls[0], SQUARE_SM)}
                    alt=""
                    width={72}
                    height={72}
                    className="w-full h-full object-cover"
                    unoptimized
                  />
                </Link>
              )
            ) : (
              <div className={cn("w-[72px] h-[72px] rounded-xl", onSheet ? "bg-foreground/10" : "bg-muted")} />
            )}
          </div>

          <div className="flex-1 min-w-0 flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <div className={cn(
                  "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold",
                  onSheet ? "bg-foreground/10 text-foreground/70" : "bg-muted text-muted-foreground",
                )}>
                  {(counterparty?.username ?? "?").charAt(0).toUpperCase()}
                </div>
                <span className="text-[13px] font-medium truncate">{username}</span>
              </div>
              <div className="flex-1" />
              <OfferStatusBadge status={status} onSheet={onSheet} />
            </div>

            {/* Both former branches tokenised to `text-foreground`, so the
                ternary is gone from the title in both mount shapes. */}
            {onOpenDetail ? (
              <p className="font-semibold text-[15px] leading-tight line-clamp-1 text-foreground">
                {offer.listing?.title ?? "Listing unavailable"}
              </p>
            ) : (
              <Link
                href={`/listings/${offer.listing?.id ?? ""}`}
                className="font-semibold text-[15px] leading-tight line-clamp-1 hover:underline text-foreground"
              >
                {offer.listing?.title ?? "Listing unavailable"}
              </Link>
            )}

            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-bold text-[18px]">
                {symbol}{amount.toFixed(2)}
              </span>
              {listPrice != null ? (
                <span className={cn("text-[13px] line-through", onSheet ? "text-foreground/50" : "text-muted-foreground")}>
                  {formatPrice(String(listPrice), offer.listing?.currency ?? offer.currency)}
                </span>
              ) : null}
              {disc != null ? (
                <span className="text-[13px] font-medium text-emerald-600">-{disc}%</span>
              ) : null}
            </div>
          </div>
        </div>

        {showTimer && timerLabel ? (
          <div
            className={cn(
              "flex items-center justify-between px-3 py-2 rounded-lg",
              isAcceptedAwaitingPayment
                ? "bg-emerald-500/10"
                : "bg-orange-500/10",
            )}
          >
            <div className="flex items-center gap-1.5">
              <Clock
                className={cn(
                  "w-4 h-4",
                  isAcceptedAwaitingPayment ? "text-emerald-600" : "text-orange-600",
                )}
              />
              <span
                className={cn(
                  "text-[13px] font-medium",
                  isAcceptedAwaitingPayment ? "text-emerald-600" : "text-orange-600",
                )}
              >
                {isAcceptedAwaitingPayment
                  ? acceptanceTimerLabel
                  : t("offers.card.expiresIn", { default: "Expires in" })}
              </span>
            </div>
            <span
              className={cn(
                "text-[14px] font-semibold",
                isAcceptedAwaitingPayment ? "text-emerald-600" : "text-orange-600",
              )}
            >
              {timerLabel}
            </span>
          </div>
        ) : null}

        {offer.message && status === "pending" ? (
          <div className={cn("px-3 py-2 rounded-lg", onSheet ? "bg-foreground/10" : "bg-muted")}>
            <p className={cn("text-[13px] line-clamp-2", onSheet ? "text-foreground/75" : "text-muted-foreground")}>
              {offer.message}
            </p>
          </div>
        ) : null}

        {showSellerActions ? (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setDeclineConfirmOpen(true)}
              disabled={pending !== null}
              className="flex-1 px-4 py-2.5 rounded-full border border-border text-[14px] font-medium active:bg-muted transition-colors"
            >
              {t("offers.bubble.decline", { default: "Decline" })}
            </button>
            <button
              type="button"
              onClick={() => setCounterOpen(true)}
              disabled={pending !== null}
              className="flex-1 px-4 py-2.5 rounded-full border border-border text-[14px] font-medium active:bg-muted transition-colors"
            >
              {t("offers.bubble.counter", { default: "Counter" })}
            </button>
            <button
              type="button"
              onClick={handleAccept}
              disabled={pending !== null}
              className="flex-1 px-4 py-2.5 rounded-full bg-foreground text-background text-[14px] font-medium active:opacity-90 transition-opacity"
            >
              {t("offers.bubble.accept", { default: "Accept" })}
            </button>
          </div>
        ) : null}

        {showBuyerCounterActions ? (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setDeclineConfirmOpen(true)}
              disabled={pending !== null}
              className="flex-1 px-4 py-2.5 rounded-full border border-border text-[14px] font-medium active:bg-muted transition-colors"
            >
              {t("offers.bubble.decline", { default: "Decline" })}
            </button>
            <button
              type="button"
              onClick={() => setCounterOpen(true)}
              disabled={pending !== null}
              className="flex-1 px-4 py-2.5 rounded-full border border-border text-[14px] font-medium active:bg-muted transition-colors"
            >
              {t("offers.bubble.counter", { default: "Counter" })}
            </button>
            <button
              type="button"
              onClick={handleAccept}
              disabled={pending !== null}
              className="flex-1 px-4 py-2.5 rounded-full bg-foreground text-background text-[14px] font-medium active:opacity-90 transition-opacity"
            >
              {t("offers.bubble.accept", { default: "Accept" })}
            </button>
          </div>
        ) : null}

        {isPendingStatus(status) && isBuyer && resolvedTab === "sent" && !showBuyerCounterActions && !buyerSentRespond ? (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleWithdraw}
              disabled={pending !== null}
              className="flex-1 px-4 py-2.5 rounded-full border border-border text-[14px] font-medium active:bg-muted transition-colors"
            >
              {t("offers.bubble.withdraw", { default: "Withdraw" })}
            </button>
          </div>
        ) : null}

        {showPayButton ? (
          <button
            type="button"
            onClick={() => {
              const evt = new CustomEvent("open-offer-checkout", { detail: { offerId: offer.id } })
              window.dispatchEvent(evt)
            }}
            className="w-full px-4 py-3 rounded-full bg-foreground text-background text-[14px] font-medium active:opacity-90 transition-opacity"
          >
            {t("offers.bubble.payNow", { default: "Pay now" })} · {symbol}{amount.toFixed(2)}
          </button>
        ) : null}
      </div>

      <OfferModal
        open={counterOpen}
        onOpenChange={setCounterOpen}
        zIndex={modalZIndex ?? 10100}
        mode={{
          kind: "counter",
          parentOfferId: offer.id,
          listingPrice: offer.listing?.price ?? null,
          currency: offer.listing?.currency ?? offer.currency,
          receivedOfferAmount: offer.amount,
        }}
        listingPreview={offer.listing ? { title: offer.listing.title, imageUrl: resolveStorageUrl(offer.listing.image_urls[0]) ?? null } : undefined}
        recipientUsername={offer.buyer?.username ?? null}
      />

      <OfferDeclineConfirmDialog
        open={declineConfirmOpen}
        onOpenChange={setDeclineConfirmOpen}
        onConfirm={handleDecline}
        loading={pending === "decline"}
      />
    </>
  )
}

function OfferStatusBadge({ status, onSheet }: { status: OfferStatus; onSheet?: boolean }) {
  const t = useTranslations("offers.status")

  // The emerald/orange rows are saturated paint — correct in both themes and
  // deliberately NOT tokenised. Only the neutral rows follow the surface.
  const neutralTone = onSheet
    ? "bg-foreground/10 text-foreground/70"
    : "bg-muted text-muted-foreground"

  const config: Record<
    OfferStatus,
    { label: string; className: string; icon?: typeof Check }
  > = {
    pending: {
      label: t("pending", { default: "Pending" }),
      className: neutralTone,
    },
    accepted: {
      label: t("accepted", { default: "Accepted" }),
      className: "bg-emerald-500/10 text-emerald-600",
      icon: Check,
    },
    declined: {
      label: t("declined", { default: "Declined" }),
      className: neutralTone,
      icon: X,
    },
    counter_offered: {
      label: t("countered", { default: "Countered" }),
      className: "bg-orange-500/10 text-orange-600",
      icon: ArrowLeftRight,
    },
    expired: {
      label: t("expired", { default: "Expired" }),
      className: neutralTone,
      icon: Clock,
    },
    withdrawn: {
      label: t("withdrawn", { default: "Withdrawn" }),
      className: neutralTone,
      icon: Clock,
    },
  }

  const { label, className, icon: Icon } = config[status]

  return (
    <span className={cn("flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium shrink-0", className)}>
      {Icon ? <Icon className="w-3 h-3" /> : null}
      {label}
    </span>
  )
}
